import { useEffect, useRef, forwardRef } from 'react';

import {  CircularProgress } from '@material-ui/core';

import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';

const ListboxWithInfiniteScroll = forwardRef(function ListboxComponent(props, refFromAutocomplete) {
    const {
        children,
        optionList,
        isLoadingMore,
        setIsLoadingMore,
        handleLoadMore,
        searchKeyword,
        scrollPositionRef,
        hasMore,
        setHasMore,
        ...otherProps
    } = props;

    const listNodeRef = useRef(null);
    const bottomRef = useRef(null);

    // Listen scroll event
    useEffect(() => {
        const listNode = listNodeRef.current;
        if (!listNode) return;

        const handleScroll = () => {
            scrollPositionRef.current = listNode.scrollTop;
        };

        listNode.addEventListener("scroll", handleScroll);
        return () => listNode.removeEventListener("scroll", handleScroll);
    }, []);

    // Set scroll position after have more optionList
    useEffect(() => {
        const listNode = listNodeRef.current;
        if (listNode && optionList.length) {
            requestAnimationFrame(() => {
                listNode.scrollTop = scrollPositionRef.current;
            });
        }
    }, [optionList]);

    // Infinite scroll
    useEffect(() => {
        if (Utils.isEmptyArray(optionList)) return;
        if (optionList.length < Constant.DRILLDOWN_SUGGEST_OPTION_VISIBLE_SIZE) return;
        if (!hasMore) return;

        const listNode = listNodeRef.current;
        const bottomNode = bottomRef.current;
        if (!listNode || !bottomNode) return;

        let timer = null;
        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0];
                const { isIntersecting, intersectionRect } = entry;
                
                if (isIntersecting && intersectionRect.height > 0 && !isLoadingMore) {
                    setIsLoadingMore(true);
                    timer = setTimeout(async () => {
                        try {
                            const result = await handleLoadMore(searchKeyword, optionList[optionList.length - 1]);
                            if (Utils.isEmptyArray(result) || result.length < Constant.DRILLDOWN_SUGGEST_OPTION_VISIBLE_SIZE) {
                                setHasMore(false);
                            }
                        } finally {
                            setIsLoadingMore(false);
                        }
                    }, 400);
                }
            },
            { root: listNode, rootMargin: '20px', threshold: 1.0 }
        );

        observer.observe(bottomNode);

        return () => {
            observer.disconnect();
            if (timer) clearTimeout(timer);
        };
    }, [optionList]);


    return (
        <div style={{ position: 'relative' }}>
            <ul
                {...otherProps}
                ref={node => listNodeRef.current = node}
                style={{
                    maxHeight: 326,
                    minHeight: 150,
                    overflow: 'auto',
                    position: 'relative',
                }}
            >
                {children}
                {/* Bottom: listen event load more */}
                <li ref={bottomRef} style={{ height: 1 }} />
            </ul>
            {isLoadingMore && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: '1px',
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <CircularProgress color="primary" size="0.8rem" />
                </div>
            )}
        </div>
    );
});

export default ListboxWithInfiniteScroll;